import { useRef, useState } from 'react';
import { Loader2, PenLine } from 'lucide-react';
import { SignaturePad } from '../shared/SignaturePad';
import { signPublicForm } from '../core/api';
import { INPUT } from './publicFormLogic';

/*
 * The SIGNING half of the public form page (docs/signature-field-scoping-2026-10.md §3): what a person with a signing link sees instead of
 * inputs. `PublicFormPage` owns the shell, the org name and every terminal state; this is the card inside it.
 *
 * - What they sign is the SERVER's rendering (`sign.view`), shown as text in a <dl> and a table, never as HTML, with the statement as real
 *   text above the name box. `sign.view_hash` goes back with the picture, so a record that moved in between is refused (409 `stale`) and the
 *   page shows the new details and asks them to read again.
 * - The pad is the shared `SignaturePad`, composed the way `SignatureSheet` composes it (name box above, pad reading the name for "Type
 *   instead", Sign disabled until name and ink). It is forked from nothing.
 * - The Sign button posts the picture and the typed name. No cookie (`signPublicForm`), no id of any kind in the page.
 */

function Details({ view }) {
  const fields = view?.fields || [];
  const lines = view?.lines;
  const rows = lines?.rows || [];
  if (!fields.length && !rows.length) return null;
  return (
    <section aria-label="What you are signing" className="mt-4 space-y-3">
      {fields.length > 0 && (
        <dl className="rounded-lg bg-paper-50 dark:bg-espresso-950 px-3 py-2.5 text-sm">
          {fields.map((f) => (
            <div key={f.key} className="flex flex-wrap gap-x-2 py-0.5">
              <dt className="text-pencil dark:text-sand">{f.label}</dt>
              <dd className="text-ink dark:text-paper min-w-0 wrap-break-word">{String(f.display ?? '—')}</dd>
            </div>
          ))}
        </dl>
      )}
      {rows.length > 0 && (
        <div className="rounded-lg border border-sand dark:border-espresso-700">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-pencil dark:text-sand">
                  {(lines.columns || []).map((c, i) => <th key={i} scope="col" className="px-3 py-2 font-medium whitespace-nowrap">{String(c)}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-sand dark:divide-espresso-700">
                {rows.map((r, ri) => (
                  <tr key={ri}>{r.map((cell, ci) => <td key={ci} className="px-3 py-2 text-ink dark:text-paper">{String(cell ?? '')}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

export function SigningCard({ token, form, notice, onSigned, onStale, onDead }) {
  const sign = form.sign;
  const [name, setName] = useState(sign.expected_signer || '');
  const [padState, setPadState] = useState({ method: 'drawn', ready: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const padRef = useRef(null);
  const canSign = name.trim().length > 0 && padState.ready && !busy;

  const submit = async () => {
    const png = padRef.current?.exportPng();
    if (!png || !name.trim() || busy) return;
    setBusy(true); setError('');
    try {
      const d = await signPublicForm(token, { image: png.image, signer_name: name.trim(), method: png.method, view_hash: sign.view_hash });
      onSigned(d?.signed || null);
    } catch (e) {
      if (e.body?.stale) await onStale();
      else if (e.status === 404) onDead(e.message);
      else setError(e.message || "That didn't save — mind trying again?");
    } finally { setBusy(false); }
  };

  return (
    <>
      <h1 className="font-display text-lg text-ink dark:text-paper">{sign.label || 'Signature'}</h1>
      <p className="mt-1 text-sm text-pencil dark:text-sand">{form.entity_label}</p>

      {notice && (
        <p role="status" className="mt-3 text-sm text-honey-ink dark:text-honey bg-honey-bg dark:bg-honey/10 rounded-lg px-3 py-2">{notice}</p>
      )}

      <Details view={sign.view} />

      <p className="mt-4 text-sm text-ink dark:text-paper border-l-2 border-sand dark:border-espresso-700 pl-3">{sign.statement}</p>

      <div className="mt-4 min-w-0">
        <label htmlFor="sign_name" className="block text-sm font-medium text-ink dark:text-paper mb-1">{sign.signer_label}</label>
        <input id="sign_name" type="text" autoComplete="off" maxLength={120} value={name} disabled={busy} className={INPUT}
               onChange={(e) => setName(e.target.value)} />
      </div>

      <div className="mt-4">
        <SignaturePad key={sign.view_hash} ref={padRef} name={name} methods={sign.methods} onChange={setPadState} disabled={busy} />
      </div>

      {error && <p role="alert" className="mt-4 text-sm text-rose-600 dark:text-rose-400">{error}</p>}

      <button type="button" onClick={submit} disabled={!canSign}
              className="mt-5 w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-sage-600 hover:bg-sage-700
                         disabled:opacity-60 px-4 py-2.5 text-base font-medium text-white transition-colors">
        {busy ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <PenLine size={16} aria-hidden="true" />}
        {busy ? 'Signing…' : 'Sign'}
      </button>
    </>
  );
}
